'use client';
import styles from './ymm.module.scss';
import { getYMMCookies } from '@/packages/sxm/ymm';
import { useEffect, useState } from 'react';
import { Model } from '@/packages/sxm/ymm/types';
import type { Props } from './interfaces';
import { Select, Option, OptionGroup } from '@/ui-components/atoms/Inputs/Select';
import { useYmmSelector } from '@/hooks/useYmmSelector';

function getInitialSelection() {
    const ymmCookie = getYMMCookies().sxm_radio.split('|');
    return {
        year: ymmCookie[1] || '',
        make: ymmCookie[2] || '',
        model: ymmCookie[3] || '',
        id: ymmCookie[4] || '',
    };
}

export function YMM(props: Props): React.JSX.Element {
    const { onCarReady, reset: resetYMM } = props;
    const [initialSelection] = useState(getInitialSelection);
    const {
        ymmData,
        ymmOptions: { years, makes, models },
        loadYears,
        loadSelectionOptions,
        selectYear,
        selectMake,
        selectModel,
    } = useYmmSelector({ initialSelection });

    const selectTheme = props?.selectTheme ? props.selectTheme : 'outline';

    useEffect(() => {
        if (resetYMM) {
            void loadYears();
            void loadSelectionOptions(initialSelection.year, initialSelection.make);
        }
    }, [initialSelection.make, initialSelection.year, loadSelectionOptions, loadYears, resetYMM]);

    useEffect(() => {
        onCarReady(ymmData);
    }, [ymmData, onCarReady]);

    function yearHandler(element: HTMLInputElement | undefined, params: { value: string }) {
        selectYear(params.value);
    }

    function makeHandler(element: HTMLInputElement | undefined, params: { value: string }) {
        selectMake(params.value);
    }

    function modelHandler(element: HTMLInputElement | undefined, params: { value: string; data?: Model }) {
        if (params.data) {
            selectModel(params.data);
        }
    }

    return (
        <div className={styles.bar + ' ' + styles[props.theme]}>
            <div className={styles.year}>
                <Select
                    label="Year"
                    type="form"
                    theme={selectTheme}
                    name="year"
                    disabled={years.length === 0}
                    onChange={yearHandler}
                    value={ymmData.year}
                    placeholder={ymmData.year}
                    dataCy="molecules-ymm-form-year-select"
                >
                    <OptionGroup>
                        {years
                            .sort()
                            .reverse()
                            .map((year, i) => (
                                <Option key={i} value={`${year}`}>{`${year}`}</Option>
                            ))}
                    </OptionGroup>
                </Select>
            </div>
            <div className={styles.make}>
                <Select
                    label="Make"
                    type="form"
                    theme={selectTheme}
                    name="make"
                    disabled={makes.length === 0}
                    onChange={makeHandler}
                    value={ymmData.make}
                    placeholder={ymmData.make}
                    dataCy="molecules-ymm-form-make-select"
                >
                    <OptionGroup>
                        {makes.map((make, i) => (
                            <Option key={i} value={make}>
                                {make}
                            </Option>
                        ))}
                    </OptionGroup>
                </Select>
            </div>
            <div className={styles.model}>
                <Select<Model>
                    label="Model"
                    type="form"
                    theme={selectTheme}
                    name="model"
                    disabled={models.length === 0}
                    onChange={modelHandler}
                    value={ymmData.model}
                    placeholder={ymmData.model}
                    dataCy="molecules-ymm-form-model-select"
                >
                    <OptionGroup>
                        {models.map((model, i) => (
                            <Option key={i} value={model.vehicleModel} object={model}>
                                {model.vehicleModel}
                            </Option>
                        ))}
                    </OptionGroup>
                </Select>
            </div>
        </div>
    );
}
